import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Bot, Lock, MoreHorizontal, Play, Plus, RefreshCw, Search, Sparkles, X } from 'lucide-react';
import { apiClient } from '../api';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import { useAssistantRecord } from '../contexts/ChatContext';
import ConfirmModal from '../components/UI/ConfirmModal';
import { monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, errMessage } from '../components/UI/ink';
import { ago, fromCve, fromIoc } from '../components/SecurityAlerts/model';
import WorkflowBuilder from '../components/workflows/WorkflowBuilder';
import RunDrawer from '../components/Playbooks/RunDrawer';
import AgentsDrawer from '../components/Playbooks/AgentsDrawer';
import { GenerateDrawer, NewPlaybookDrawer } from '../components/Playbooks/CreateDrawers';
import {
  HOST_TOOLS, RUN_STATE, TARGET_LABEL, TARGET_TYPES, barColor, barFromStates, fmtDur, parseAgent, parseWorkflow, runDurationMs, runState, stamp,
} from '../components/Playbooks/model';

// One page instead of three tabs: playbooks and recent runs on the left, the
// selected playbook docked on the right, runs and agents in drawers.
// ?playbook= selects a playbook, ?run= opens a run, ?asset_id= pre-fills the target.
const Label = ({ children }) => <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>;
const Marker = ({ st, size = 8 }) => {
  const RS = RUN_STATE[st];
  return <span className="shrink-0 box-border rounded-sm" style={{ width: size, height: size, background: RS.mk, border: `1px solid ${RS.mkB || 'transparent'}` }} />;
};
const fieldStyle = { background: 'var(--surf-bg)', borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' };
const TARGET_HINT = {
  finding: 'Finding id, e.g. fnd-3a91c0e2',
  ioc: 'IoC alert number, e.g. 1042',
  cve: 'CVE alert number, e.g. 318',
  asset: 'Hostname or IP, e.g. web-prod-01',
  none: 'Runs without a specific target',
};

const PlaybookMenu = ({ onDelete }) => {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!pos) return undefined;
    const close = (e) => { if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setPos(null); };
    const onKey = (e) => { if (e.key === 'Escape') setPos(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', onKey); };
  }, [pos]);
  const toggle = () => {
    if (pos) { setPos(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    setPos({ right: window.innerWidth - r.right, top: r.bottom + 4 });
  };
  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} aria-label="More actions" aria-haspopup="menu" aria-expanded={!!pos} className="w-8 h-8 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
        <MoreHorizontal className="w-[15px] h-[15px]" />
      </button>
      {pos && createPortal(
        <div ref={menuRef} role="menu" className="fixed z-40 min-w-[180px] p-1 rounded-[10px] border" style={{ ...pos, background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)', boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}>
          <button type="button" role="menuitem" onClick={() => { setPos(null); onDelete(); }} className="w-full h-10 px-3 rounded-md text-left text-[13px] hover:bg-[color:var(--surf-raised)]" style={{ color: 'var(--sev-crit-fg)' }}>Delete playbook</button>
        </div>,
        document.body,
      )}
    </>
  );
};

const Playbooks = () => {
  const { permissions: myPerms } = useAuth();
  const can = useCallback(p => (myPerms || []).includes(p), [myPerms]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [workflows, setWorkflows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [runs, setRuns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('all');
  const [runScope, setRunScope] = useState('all');
  const [showAllRuns, setShowAllRuns] = useState(false);

  const [tType, setTType] = useState('finding');
  const [tId, setTId] = useState('');
  const [context, setContext] = useState('');
  const [showContext, setShowContext] = useState(false);
  const [options, setOptions] = useState({});
  const [starting, setStarting] = useState(false);

  const [drawer, setDrawer] = useState(null); // 'agents' | 'new' | 'generate'
  const [builder, setBuilder] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const noticeTimer = useRef(null);

  const selectedId = searchParams.get('playbook');
  const runId = searchParams.get('run');
  useAssistantRecord(runId && { kind: 'run', id: runId, label: `RUN-${runId.slice(0, 8).toUpperCase()}` });
  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v); else next.delete(k);
    setSearchParams(next, { replace: true });
  };

  const flash = (kind, text) => {
    setNotice({ kind, text });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 6000);
  };
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const loadRuns = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/workflows/runs');
      setRuns(res.data?.runs || []);
    } catch { /* keep last */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const [w, a, s, r, t] = await Promise.allSettled([
      apiClient.get('/api/workflows'),
      apiClient.get('/api/workflows/agents'),
      apiClient.get('/api/workflows/skills'),
      apiClient.get('/api/workflows/runs'),
      apiClient.get('/api/workflows/templates'),
    ]);
    if (w.status === 'fulfilled') setWorkflows((w.value.data?.workflows || []).map(parseWorkflow));
    else flash('error', errMessage(w.reason, 'Could not load playbooks.'));
    if (a.status === 'fulfilled') setAgents((a.value.data?.agents || []).map(parseAgent));
    if (s.status === 'fulfilled') setSkills(s.value.data?.skills || []);
    if (r.status === 'fulfilled') setRuns(r.value.data?.runs || []);
    if (t.status === 'fulfilled') setTemplates(t.value.data?.templates || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    orgApi.getMembers().then(m => setMembers(m?.members || m?.users || [])).catch(() => {});
    apiClient.get('/api/servers?state=REGISTERED').then(r => setServers(r.data?.servers || [])).catch(() => {});
  }, []);

  // Keep the runs list fresh while anything is in flight.
  const active = runs.some(r => r.status === 'running' || r.status === 'waiting_approval');
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(loadRuns, 10000);
    return () => clearInterval(id);
  }, [active, loadRuns]);

  // ?asset_id= from an asset page pre-fills the target.
  const assetParam = searchParams.get('asset_id');
  useEffect(() => {
    if (!assetParam || !servers.length) return;
    const s = servers.find(x => x.id === assetParam);
    if (s) { setTType('asset'); setTId(s.hostname || s.ip_address || s.id); }
  }, [assetParam, servers]);

  // Suggestions for the target box, loaded per type on first use.
  useEffect(() => {
    if (options[tType] || tType === 'none') return;
    const set = (list) => setOptions(o => ({ ...o, [tType]: list }));
    const hostOf = (id) => servers.find(s => s.id === id)?.hostname || id || 'unknown';
    if (tType === 'finding') {
      apiClient.get('/api/findings?status=new,triaged,investigating,contained&limit=100')
        .then(r => set((r.data?.findings || []).map(f => [f.id, `${f.title} · ${f.server_hostname || f.server_id || ''}`])))
        .catch(() => set([]));
    } else if (tType === 'ioc') {
      apiClient.get('/api/alerts/ioc?resolved=0')
        .then(r => set((r.data?.ioc_alerts || []).slice(0, 100).map(a => { const i = fromIoc(a, hostOf); return [String(a.id), `${i.title} · ${i.host}`]; })))
        .catch(() => set([]));
    } else if (tType === 'cve') {
      apiClient.get('/api/alerts/cve?resolved=0')
        .then(r => set((r.data?.cve_alerts || []).slice(0, 100).map(a => { const i = fromCve(a, hostOf); return [String(a.id), `${i.title} · ${i.host}`]; })))
        .catch(() => set([]));
    } else if (tType === 'asset') {
      set(servers.map(s => [s.hostname || s.ip_address || s.id, [s.ip_address, s.os_info].filter(Boolean).join(' · ')]));
    }
  }, [tType, options, servers]);

  const agentName = useCallback((id) => agents.find(a => a.id === id)?.name || id, [agents]);
  // A phase can call exactly its agent's tools; any tools stored on older phases are ignored by the Worker.
  const agentTools = useCallback((id) => agents.find(a => a.id === id)?.tools || [], [agents]);
  const nameOf = useCallback((email) => members.find(m => m.email === email)?.full_name || email, [members]);

  const runsByWf = useMemo(() => {
    const map = {};
    runs.forEach(r => { (map[r.workflow_id] = map[r.workflow_id] || []).push(r); });
    return map;
  }, [runs]);

  const sources = [...new Set(workflows.map(w => w.source).filter(Boolean))];
  const q = search.trim().toLowerCase();
  const visible = workflows
    .filter(w => source === 'all' || w.source === source)
    .filter(w => !q || [w.name, w.description, ...w.phases.flatMap(p => [p.name, p.agent_id, agentName(p.agent_id), ...agentTools(p.agent_id)])]
      .some(v => v && String(v).toLowerCase().includes(q)));

  const wf = workflows.find(w => w.id === selectedId) || visible[0] || workflows[0] || null;
  const waitingRuns = runs.filter(r => r.status === 'waiting_approval');
  const runningCount = runs.filter(r => r.status === 'running').length;
  const builtIn = agents.filter(a => a.source !== 'custom').length;
  const customAgents = agents.length - builtIn;

  const summary = loading && !workflows.length ? 'Loading…' : [
    `${workflows.length} playbook${workflows.length === 1 ? '' : 's'}`,
    waitingRuns.length && `${waitingRuns.length} run${waitingRuns.length === 1 ? '' : 's'} waiting for approval`,
    `${runningCount} running`,
    agents.length && `${builtIn} built-in agent${builtIn === 1 ? '' : 's'}, ${customAgents} custom`,
  ].filter(Boolean).join(' · ');

  const execute = async (w, target) => {
    setStarting(true);
    try {
      const body = { target_context: target.context?.trim() || undefined };
      if (target.type && target.type !== 'none' && target.id?.trim()) {
        body.target_type = target.type;
        body.target_id = target.id.trim();
      }
      const res = await apiClient.post(`/api/workflows/${w.id}/execute`, body);
      await loadRuns();
      if (res.data?.run_id) setParam('run', res.data.run_id);
      return true;
    } catch (err) {
      flash('error', errMessage(err, 'Could not start the run.'));
      return false;
    } finally {
      setStarting(false);
    }
  };

  const runSelected = async () => {
    if (!wf) return;
    const ok = await execute(wf, { type: tType, id: tId, context });
    if (ok) { setContext(''); setShowContext(false); }
  };

  const deleteWorkflow = async () => {
    const w = confirmDelete;
    setConfirmDelete(null);
    try {
      await apiClient.delete(`/api/workflows/${w.id}`);
      if (selectedId === w.id) setParam('playbook', null);
      flash('ok', `${w.name} deleted.`);
      load();
    } catch (err) {
      flash('error', errMessage(err, 'Could not delete the playbook.'));
    }
  };

  // Right-pane facts for the selected playbook.
  const wfRuns = wf ? runsByWf[wf.id] || [] : [];
  const gates = wf ? wf.phases.filter(p => p.approval_required).length : 0;
  const changesHosts = wf ? wf.phases.some(p => agentTools(p.agent_id).some(t => HOST_TOOLS.includes(t))) : false;
  const runHint = gates
    ? `Runs pause at the approval gate.${changesHosts ? ' Anything a containment phase queues still waits in Response Actions for a person to confirm.' : ''}`
    : changesHosts
      ? 'No approval gate. Host-changing tools still only queue actions that wait in Response Actions for a person to confirm.'
      : 'No approval gate and no tools that change a host. It only reads and reports.';
  const triggers = wf ? (wf.trigger_examples.length ? wf.trigger_examples : wf.use_case && wf.use_case !== wf.description ? [wf.use_case] : []) : [];

  const shownRuns = (runScope === 'this' && wf ? wfRuns : runs);
  const runRows = showAllRuns ? shownRuns : shownRuns.slice(0, 8);
  const firstWaiting = waitingRuns[0];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-4" style={{ color: 'var(--ink-text)' }}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>INTELLIGENCE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Playbooks</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button type="button" onClick={load} disabled={loading} aria-label="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border disabled:opacity-60" style={secondaryBtnStyle}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />
          </button>
          <button type="button" onClick={() => setDrawer('agents')} className={secondaryBtn} style={secondaryBtnStyle}><Bot className="w-4 h-4" strokeWidth={1.8} />Agents</button>
          <button type="button" onClick={() => setDrawer('generate')} className={secondaryBtn} style={secondaryBtnStyle}><Sparkles className="w-4 h-4" strokeWidth={1.8} />Generate with AI</button>
          <button type="button" onClick={() => setDrawer('new')} className={primaryBtn} style={primaryBtnStyle}><Plus className="w-4 h-4" strokeWidth={2} />New playbook</button>
        </div>
      </div>

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={notice.kind === 'error'
          ? { background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' }
          : { background: 'var(--sev-good-bg)', borderColor: 'var(--sev-good-fg)' }}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* A run waiting on a person */}
      {firstWaiting && (
        <section aria-label="Waiting for approval" className="relative min-h-16 box-border flex flex-wrap items-center gap-4 py-3 pl-5 pr-3 rounded-xl border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)' }}>
          <span aria-hidden="true" className="absolute -left-px top-5 w-px h-6" style={{ background: 'var(--accent)' }} />
          <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg" style={{ background: 'var(--sev-high-bg)', color: 'var(--sev-high-fg)' }}><Lock className="w-4 h-4" strokeWidth={1.9} /></span>
          <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
            <span className="text-sm font-medium">{firstWaiting.workflow_name || 'A playbook'} paused before {firstWaiting.gate_phase && firstWaiting.gate_phase !== 'null' ? firstWaiting.gate_phase : 'an approval gate'}{waitingRuns.length > 1 ? ` · ${waitingRuns.length - 1} more waiting` : ''}</span>
            <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>
              {[firstWaiting.id.slice(0, 8).toUpperCase(), firstWaiting.target_id && firstWaiting.target_id !== 'null' ? firstWaiting.target_id : null, (ago(firstWaiting.started_at) === 'now' || !ago(firstWaiting.started_at) ? 'started just now' : `started ${ago(firstWaiting.started_at)} ago`), firstWaiting.started_by ? `by ${nameOf(firstWaiting.started_by)}` : null].filter(Boolean).join(' · ')}
            </span>
          </span>
          <button type="button" onClick={() => setParam('run', firstWaiting.id)} className="h-10 px-4 rounded-[9px] text-[13.5px] font-semibold" style={primaryBtnStyle}>Review and approve</button>
        </section>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_500px] gap-5 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          {/* Playbooks */}
          <section aria-label="Playbooks" className="rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
            <div className="min-h-[52px] box-border flex flex-wrap items-center gap-2 py-2 pl-4 pr-2.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
              <span className="flex-grow font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{visible.length} PLAYBOOK{visible.length === 1 ? '' : 'S'}</span>
              <div className="w-[210px] h-[34px] box-border flex items-center gap-2 px-2.5 rounded-lg border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                <Search className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
                <input type="search" aria-label="Search playbooks" placeholder="Name, agent or tool" value={search} onChange={e => setSearch(e.target.value)} className="flex-grow min-w-0 h-8 bg-transparent border-0 outline-none text-[13px]" style={{ color: 'var(--ink-text)' }} />
              </div>
              {sources.length > 1 && (
                <>
                  <label htmlFor="pb-src" className="sr-only">Source</label>
                  <select id="pb-src" value={source} onChange={e => setSource(e.target.value)} className="h-[34px] box-border px-2 rounded-lg border text-[13px]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                    <option value="all">All sources</option>
                    {sources.map(s => <option key={s} value={s}>{s === 'file' ? 'From file' : s[0].toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </>
              )}
            </div>
            {visible.length === 0 && (
              <div className="py-12 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
                {loading ? 'Loading playbooks…' : workflows.length ? 'No playbooks match that search.' : 'No playbooks yet. Start from a template or generate one with AI.'}
              </div>
            )}
            {visible.map(w => {
              const on = wf?.id === w.id;
              const wr = runsByWf[w.id] || [];
              const last = wr[0];
              const st = last ? runState(last) : 'never';
              const durs = wr.filter(r => r.status === 'completed').map(runDurationMs).filter(Boolean);
              const avg = durs.length ? fmtDur(durs.reduce((a, b) => a + b, 0) / durs.length) : null;
              return (
                <div key={w.id} className="relative">
                  <button type="button" onClick={() => setParam('playbook', w.id)} aria-pressed={on} className="w-full min-h-[72px] box-border flex items-center gap-4 px-4 py-3 border-b text-left" style={{ borderColor: 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'transparent' }}>
                    <span className="flex-grow min-w-0 flex flex-col gap-[7px]">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">{w.name}</span>
                        <span className="h-[18px] shrink-0 inline-flex items-center px-1.5 rounded-[5px] border font-mono text-[9.5px] tracking-[0.08em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>{w.source === 'file' ? 'FILE' : 'CUSTOM'}</span>
                      </span>
                      <span className="flex items-center gap-[5px] font-mono text-[11px] whitespace-nowrap overflow-hidden" style={{ color: 'var(--ink-faint)' }}>
                        {w.phases.map((p, i) => (
                          <span key={p.phase_id || i} className="inline-flex items-center gap-[5px] shrink-0">
                            {i > 0 && <span aria-hidden="true" style={{ color: 'var(--surf-line-strong)' }}>›</span>}
                            {p.approval_required && <Lock className="w-[11px] h-[11px]" strokeWidth={2.2} aria-label="approval gate" style={{ color: 'var(--ink-muted)' }} />}
                            <span style={{ color: p.approval_required ? 'var(--ink-muted)' : undefined }}>{agentName(p.agent_id)}</span>
                          </span>
                        ))}
                      </span>
                    </span>
                    <span className="w-[170px] shrink-0 hidden sm:flex flex-col items-end gap-1.5">
                      <span className="flex items-center gap-[7px] text-[12.5px]" style={{ color: 'var(--ink-muted)' }}><Marker st={st} size={7} />{RUN_STATE[st].label}{last ? ` · ${ago(last.started_at)}` : ''}</span>
                      <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{wr.length ? `${wr.length} run${wr.length === 1 ? '' : 's'}${avg ? ` · avg ${avg}` : ''}` : 'No runs yet'}</span>
                    </span>
                  </button>
                  {on && <span aria-hidden="true" className="absolute right-0 top-6 w-px h-6" style={{ background: 'var(--accent)' }} />}
                </div>
              );
            })}
          </section>

          {/* Recent runs */}
          <section aria-label="Recent runs" className="rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
            <div className="h-[46px] shrink-0 box-border flex items-center gap-3 px-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
              <span className="flex-grow font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>RECENT RUNS</span>
              <div role="group" aria-label="Which runs" className="flex gap-[3px] p-[3px] rounded-lg border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                {[['all', 'All playbooks'], ['this', 'This playbook']].map(([id, l]) => (
                  <button key={id} type="button" onClick={() => setRunScope(id)} aria-pressed={runScope === id} disabled={id === 'this' && !wf} className="h-7 px-2.5 rounded-md border text-xs disabled:opacity-50" style={{ background: runScope === id ? 'var(--surf-raised)' : 'transparent', borderColor: runScope === id ? 'var(--surf-line)' : 'transparent', color: runScope === id ? 'var(--ink-text)' : 'var(--ink-muted)' }}>{l}</button>
                ))}
              </div>
            </div>
            {shownRuns.length === 0 && (
              <div className="py-10 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>{loading ? 'Loading runs…' : 'No runs yet. Pick a playbook and run it against a finding, alert or asset.'}</div>
            )}
            {runRows.map(r => {
              const st = runState(r);
              const bar = barFromStates(r);
              const target = r.target_id && r.target_id !== 'null' ? r.target_id : null;
              const meta = [r.id.slice(0, 8).toUpperCase(), target, r.started_by ? nameOf(r.started_by) : null].filter(Boolean).join(' · ');
              return (
                <button key={r.id} type="button" onClick={() => setParam('run', r.id)} className="w-full min-h-14 box-border flex items-center gap-3.5 px-4 py-2 border-b last:border-b-0 text-left hover:bg-[color:var(--surf-raised)]" style={{ borderColor: 'var(--surf-line)' }}>
                  <span className="w-[158px] shrink-0 hidden sm:flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}><Marker st={st} />{RUN_STATE[st].label}</span>
                  <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
                    <span className="flex items-center gap-2 text-[13.5px] font-medium truncate"><span className="sm:hidden"><Marker st={st} /></span>{r.workflow_name || 'Deleted playbook'}</span>
                    <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{meta}</span>
                  </span>
                  <span className="w-[72px] shrink-0 flex gap-0.5" aria-hidden="true">
                    {bar.map((s, i) => <span key={i} className="flex-grow h-[3px] rounded-sm" style={{ background: barColor(s) }} />)}
                  </span>
                  <span className="w-11 shrink-0 text-right font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{ago(r.started_at)}</span>
                </button>
              );
            })}
            {shownRuns.length > 8 && (
              <button type="button" onClick={() => setShowAllRuns(v => !v)} className="h-10 text-[12.5px] border-t" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-link)' }}>
                {showAllRuns ? 'Show fewer' : `Show all ${shownRuns.length} runs`}
              </button>
            )}
          </section>
        </div>

        {/* Selected playbook */}
        <aside aria-label="Playbook details" className="rounded-[14px] border flex flex-col overflow-hidden xl:sticky xl:top-6 xl:max-h-[calc(100vh-48px)]" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          {!wf ? (
            <div className="min-h-[420px] flex flex-col items-center justify-center gap-2.5 p-8 text-center">
              <span className="text-[15px] font-semibold">{loading ? 'Loading…' : 'No playbook selected'}</span>
              {!loading && <span className="text-[13px] max-w-[280px]" style={{ color: 'var(--ink-muted)' }}>Create one to see its phases and run it here.</span>}
            </div>
          ) : (
            <>
              <div className="shrink-0 flex flex-col gap-2.5 px-[22px] pt-[18px] pb-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                <div className="flex items-center gap-2">
                  <span className="flex-grow font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-link)' }}>PLAYBOOK · {wf.source === 'file' ? 'FROM FILE' : 'CUSTOM'}</span>
                  {wf.source !== 'file' && <button type="button" onClick={() => setBuilder(wf)} className="h-8 px-3 rounded-lg border text-[13px]" style={secondaryBtnStyle}>Edit</button>}
                  {wf.source !== 'file' && <PlaybookMenu onDelete={() => setConfirmDelete(wf)} />}
                </div>
                <h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em] leading-tight">{wf.name}</h2>
                {wf.description && <p className="m-0 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{wf.description}</p>}
                <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                  {[
                    `${wf.phases.length} phase${wf.phases.length === 1 ? '' : 's'}`,
                    gates ? `${gates} approval gate${gates === 1 ? '' : 's'}` : 'no approval gate',
                    wfRuns.length ? `${wfRuns.length} run${wfRuns.length === 1 ? '' : 's'}` : 'never run',
                    wf.updated_at ? `updated ${stamp(wf.updated_at)}` : null,
                    wf.created_by ? `by ${nameOf(wf.created_by)}` : null,
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3.5 px-[22px] py-3.5">
                <Label>PHASES · RUN IN ORDER</Label>
                <div className="flex flex-col">
                  {wf.phases.map((p, i) => (
                    <div key={p.phase_id || i} className="grid grid-cols-[26px_minmax(0,1fr)] gap-3">
                      <span className="flex flex-col items-center">
                        <span className="w-6 h-6 shrink-0 box-border flex items-center justify-center rounded-full border font-mono text-[11px]" style={{ borderColor: 'var(--surf-line-strong)', background: 'var(--surf-bg)', color: 'var(--ink-muted)' }}>{i + 1}</span>
                        {i < wf.phases.length - 1 && <span className="flex-grow w-px" style={{ background: 'var(--surf-line)' }} />}
                      </span>
                      <span className="min-w-0 flex flex-col gap-[5px] pt-0.5 pb-3.5">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-[13.5px] font-medium">{p.name}</span>
                          <span className="h-5 inline-flex items-center px-[7px] rounded-[5px] text-[11.5px]" style={{ background: 'var(--surf-raised)', color: 'var(--ink-muted)' }}>{agentName(p.agent_id)}</span>
                          {p.approval_required && (
                            <span className="h-5 inline-flex items-center gap-[5px] px-[7px] rounded-[5px] border font-mono text-[10px] tracking-[0.06em]" style={{ borderColor: 'var(--surf-line-strong)' }}>
                              <Lock className="w-2.5 h-2.5" strokeWidth={2.4} />APPROVAL GATE
                            </span>
                          )}
                        </span>
                        {p.purpose && <span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-muted)' }}>{p.purpose}</span>}
                        <span className="flex flex-wrap gap-[5px]">
                          {agentTools(p.agent_id).length ? agentTools(p.agent_id).map(t => (
                            <span key={t} title={skills.find(s => s.id === t)?.description} className="h-[22px] inline-flex items-center px-[7px] rounded-md border font-mono text-[11px]" style={{ borderColor: 'var(--surf-line)' }}>{t}</span>
                          )) : <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>No tools · works from earlier phases’ output</span>}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
                {triggers.length > 0 && (
                  <div className="flex flex-col gap-1.5">
                    <Label>WHEN TO RUN IT</Label>
                    {triggers.map(t => <span key={t} className="flex gap-2 text-[12.5px] leading-snug" style={{ color: 'var(--ink-muted)' }}><span aria-hidden="true" style={{ color: 'var(--ink-faint)' }}>–</span>{t}</span>)}
                  </div>
                )}
              </div>

              <div className="shrink-0 flex flex-col gap-2 px-[22px] pt-3.5 pb-4 border-t" style={{ borderColor: 'var(--surf-line)' }}>
                <span className="flex justify-between items-center">
                  <Label>RUN AGAINST</Label>
                  <button type="button" onClick={() => setShowContext(v => !v)} aria-expanded={showContext} className="h-6 text-[12.5px]" style={{ color: 'var(--ink-link)' }}>{showContext ? 'Hide context' : '+ Add context for the agents'}</button>
                </span>
                {showContext && (
                  <>
                    <label htmlFor="run-context" className="sr-only">Context for the agents</label>
                    <textarea id="run-context" rows={3} value={context} onChange={e => setContext(e.target.value)} placeholder="Anything the agents should know, e.g. a planned deploy went out at 10:00" className="box-border px-3 py-2 rounded-[9px] border text-[13px] leading-normal resize-none outline-none focus:border-[color:var(--accent)]" style={fieldStyle} />
                  </>
                )}
                <div className="flex gap-2">
                  <label htmlFor="tgt-type" className="sr-only">Target type</label>
                  <select id="tgt-type" value={tType} onChange={e => { setTType(e.target.value); setTId(''); }} className="w-[118px] shrink-0 h-[42px] box-border px-2 rounded-[9px] border text-[13px]" style={fieldStyle}>
                    {TARGET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <label htmlFor="tgt-id" className="sr-only">{TARGET_LABEL[tType] || 'Target'}</label>
                  <input
                    id="tgt-id"
                    type="text"
                    list={tType !== 'none' ? `tgt-${tType}` : undefined}
                    value={tId}
                    onChange={e => setTId(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') runSelected(); }}
                    disabled={tType === 'none'}
                    placeholder={TARGET_HINT[tType]}
                    className="flex-grow min-w-0 h-[42px] box-border px-3 rounded-[9px] border font-mono text-[12.5px] outline-none focus:border-[color:var(--accent)] disabled:opacity-60"
                    style={fieldStyle}
                  />
                  {tType !== 'none' && (
                    <datalist id={`tgt-${tType}`}>
                      {(options[tType] || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </datalist>
                  )}
                  <button type="button" onClick={runSelected} disabled={starting || (tType !== 'none' && !tId.trim() && !context.trim())} className="h-[42px] shrink-0 flex items-center gap-[7px] px-4 rounded-[9px] text-[13.5px] font-semibold disabled:opacity-50" style={primaryBtnStyle}>
                    <Play className="w-[13px] h-[13px]" fill="currentColor" />{starting ? 'Starting…' : 'Run'}
                  </button>
                </div>
                <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>{runHint}</span>
              </div>
            </>
          )}
        </aside>
      </div>

      <RunDrawer
        runId={runId}
        workflows={workflows}
        agentName={agentName}
        nameOf={nameOf}
        servers={servers}
        agentTools={agentTools}
        canApprove={can('playbooks:approve')}
        onClose={() => setParam('run', null)}
        onChanged={loadRuns}
        onRunAgain={(r) => {
          const w = workflows.find(x => x.id === r.workflow_id);
          if (w) execute(w, { type: r.target_type, id: r.target_id && r.target_id !== 'null' ? r.target_id : '', context: r.target_context && r.target_context !== 'null' ? r.target_context : '' });
        }}
      />
      <AgentsDrawer
        open={drawer === 'agents'}
        agents={agents}
        skills={skills}
        workflows={workflows}
        nameOf={nameOf}
        onClose={() => setDrawer(null)}
        onChanged={load}
        onPickWorkflow={(id) => { setDrawer(null); setParam('playbook', id); }}
      />
      <NewPlaybookDrawer
        open={drawer === 'new'}
        templates={templates}
        agentName={agentName}
        onClose={() => setDrawer(null)}
        onStart={(draft) => { setDrawer(null); setBuilder(draft); }}
      />
      <GenerateDrawer
        open={drawer === 'generate'}
        onClose={() => setDrawer(null)}
        onGenerated={(draft) => { setDrawer(null); setBuilder(draft); }}
      />
      {builder !== null && (
        <WorkflowBuilder
          workflow={builder.id || builder.name ? builder : null}
          agents={agents}
          onClose={() => setBuilder(null)}
          onSave={async () => { setBuilder(null); await load(); }}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmDelete}
        danger
        title={`Delete ${confirmDelete?.name}?`}
        message="The playbook is removed. Past runs stay in the history."
        confirmLabel="Delete playbook"
        onConfirm={deleteWorkflow}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default Playbooks;
